import React from 'react';
import { PaginationState, Table } from '@tanstack/react-table';
import { Poppins, Spacer } from 'src/common';
import { PagNavButton, PagNumberButton } from 'src/common/pagination/PagNavButton';
import { GenericSelect } from '../../select/GenericSelect';
import * as S from '../GenericTable.styles';

interface GTPaginationProps<T> {
  table: Table<T>;
  isMd: boolean;
  scrollToTable: () => void;
  start: number;
  end: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
}

export const GTPagination = <T,>({
  table,
  isMd,
  scrollToTable,
  start,
  end,
  pagination,
  setPagination,
}: GTPaginationProps<T>) => {
  const selectComponent = () => {
    return (
      <GenericSelect
        value={pagination.pageSize}
        onChange={(opt) => {
          const value = opt.value as number;
          const newPageIndex = Math.floor((pagination.pageIndex * pagination.pageSize) / value);
          setPagination({ pageIndex: newPageIndex, pageSize: value });
        }}
        white
        width={120}
        placeholder="Select page size"
        options={[
          {
            label: '20',
            value: 20,
          },
          {
            label: '50',
            value: 50,
          },
          {
            label: '100',
            value: 100,
          },
        ]}
      />
    );
  };

  return (
    <>
      <Spacer $px={20} />
      <S.PaginationContainer data-cy="gt-pagination">
        <div className="pag-controls">
          {!isMd && selectComponent()}
          <div className="pag-controls__right">
            <PagNavButton
              onClick={() => {
                table.firstPage();
                scrollToTable();
              }}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </PagNavButton>
            <PagNavButton
              className="pag-controls__right__main-btn"
              onClick={() => {
                table.previousPage();
                scrollToTable();
              }}
              disabled={!table.getCanPreviousPage()}
            >
              {'<'}
              {!isMd && 'Previous'}
            </PagNavButton>

            {Array.from({ length: end - start + 1 }).map((_, i) => {
              const pageIndex = start + i;
              return (
                <PagNumberButton
                  key={pageIndex}
                  selected={pageIndex === table.getState().pagination.pageIndex}
                  onClick={() => {
                    table.setPageIndex(pageIndex);
                    scrollToTable();
                  }}
                >
                  {pageIndex + 1}
                </PagNumberButton>
              );
            })}
            <PagNavButton
              className="pag-controls__right__main-btn"
              onClick={() => {
                table.nextPage();
                scrollToTable();
              }}
              disabled={!table.getCanNextPage()}
            >
              {!isMd && 'Next'} {'>'}
            </PagNavButton>
            <PagNavButton
              onClick={() => {
                table.lastPage();
                scrollToTable();
              }}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </PagNavButton>
          </div>
        </div>
        <Spacer $px={4} />
        <div className="pag-stats">
          {isMd && selectComponent()}
          <div className="pag-stats__end">
            <Poppins px={14} color="cflowerBlue">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
            </Poppins>
            <Poppins px={14} color="cflowerBlue">
              Showing {table.getRowModel().rows.length.toLocaleString()} of {table.getRowCount().toLocaleString()} Rows
            </Poppins>
          </div>
        </div>
      </S.PaginationContainer>
    </>
  );
};
